import  { useState, useEffect } from 'react'
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  TimePicker,
  Row,
  Col,
  Button
} from 'antd'
import { classesEventStore } from 'state/classes/classesStore'
import {
  useCreateClassesDiscountCode,
  useUpdateClassesDiscountCode,
} from 'api/Classes/Step6Tickets/Tickets'
import { DiscountStatus, DiscountType } from 'types/Classes/Classes'
import dayjs from 'dayjs'
import closeIcon from 'assets/Images/close.svg'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { infoMessage } from 'api/global'
import { validateClassDiscountCode } from 'helper/ClassDiscountCode'


const DiscountCode = ({ modalOpen, modalClose, data }: any) => {
  const classesData = classesEventStore(store => store.classesData) as any
  const { _id, admissionTickets, additionalOptions, dropInTicket } = classesData || {}

  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [discountType, setDiscountType] = useState<'percent' | 'fixed'>('percent')

  const { mutate: updateDiscountMutation } = useUpdateClassesDiscountCode()
  const { mutate: createDiscountMutation } = useCreateClassesDiscountCode()

  const groupedOptions = [
    {
      label: <h3 className="text-14 w-600">Admission ticket</h3>,
      options: admissionTickets?.map((ticket: any) => ({
        value: ticket._id,
        label: ticket.name,
      })) || [],
    },
    {
      label: <h3 className="text-14 w-600">Additional ticket</h3>,
      options: additionalOptions?.map((ticket: any) => ({
        value: ticket._id,
        label: ticket.name,
      })) || [],
    },
    ...(dropInTicket ? [{
      label: <h3 className="text-14 w-600">Drop-in ticket</h3>,
      options: [{
        value: dropInTicket._id,
        label: dropInTicket.name,
      }],
    }] : []),
  ]

  const formSchema = {
    code: '',
    type: 'percent',
    discount: 0,
    admissionTicketIds: [],
    additionalOptionIds: [],
    dropInTicketId: undefined,
    totalLimit: undefined,
    limitPerFamily: undefined,
    expiry: undefined,
    'expiry-time': undefined,
    note: ''
  }

  useEffect(() => {
    if (modalOpen && data) {
      const type = data.type === 'usd' ? 'fixed' : 'percent'
      setDiscountType(type)
      form.setFieldsValue({
        code: data.code,
        type: data.type || 'percent',
        discount: data.discount,
        admissionTicketIds: data.admissionTicketIds || [],
        additionalOptionIds: data.additionalOptionIds || [],
        dropInTicketId: data.dropInTicketId,
        totalLimit: data.totalLimit,
        limitPerFamily: data.limitPerFamily,
        expiry: data.expiry ? dayjs(data.expiry) : undefined,
        'expiry-time': data.expiry ? dayjs(data.expiry) : undefined,
        note: data.note
      })
    } else if (modalOpen) {
      form.resetFields()
      setDiscountType('percent')
    }
  }, [modalOpen, data])

  const onFinish = (values: any) => {
    if (!_id) return
    // Validate form fields
    const validationResult = validateClassDiscountCode(values, infoMessage)
    if (!validationResult) {
      return
    }

    setIsLoading(true)

    let expirationDate = null
    if (values.expiry && values['expiry-time']) {
      expirationDate = `${values.expiry.format('YYYY-MM-DD')}T${values['expiry-time'].format('HH:mm:ss')}Z`
    }

    const payload = {
      code: values.code,
      type: values.type === 'fixed' ? 'usd' : 'percent' as DiscountType,
      discount: values.discount,
      admissionTicketIds: values.admissionTicketIds || [],
      additionalOptionIds: values.additionalOptionIds || [],
      dropInTicketId: values.dropInTicketId,
      totalLimit: values.totalLimit,
      limitPerFamily: values.limitPerFamily,
      expiry: expirationDate,
      note: values.note
    }

    const onSuccess = (response: any) => {
      modalClose(false)
      setIsLoading(false)
    }

    const onError = (error: any) => {
      setIsLoading(false)
    }

    if (data) {
      updateDiscountMutation({ payload, discountCodeId: data._id }, { onSuccess, onError })
    } else {
      createDiscountMutation({ payload, classId: _id }, { onSuccess, onError })
    }
  }

  return (
    <div>
      <Modal
        open={modalOpen}
        onCancel={() => modalClose(false)}
        className="discount-modal"
        footer={false}
        centered
      >
        <div className="flex-center pt-20px justify-between ">
          <h2 className="w-600 text-18">Add discount code</h2>
          <img
            src={closeIcon}
            alt="close-button"
            onClick={() => modalClose(false)}
            className="close button pointer"
          />
        </div>
        <div className="h-30rem  overflow-auto-y overflow-hidden-x scroller">
          <Form
            form={form}
            layout="vertical"
            initialValues={formSchema}
            onFinish={onFinish}
          >
            <Row gutter={10}>
              <Col span={24}>
                <label className="input-label mt-20px">
                  Discount Code
                </label>
                <Form.Item className="form-control" name="code" rules={[{ required: true, message: 'Please enter discount code' }]}>
                  <Input
                    placeholder="Enter discount code"
                    className="form-control-field custom-input-field"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={10}>
              <Col span={24}>
                <label className="input-label">Tickets</label>
                <Form.Item
                  className="pointer selector-input"
                  name="admissionTicketIds"
                  rules={[{ required: true, message: 'Please select ticket type' }]}
                >
                  <Select
                    className="select-input-field type-select-field"
                    placeholder="Select"
                    optionLabelProp="label"
                    mode="multiple"
                    options={groupedOptions}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={10} align="middle">
              <Col span={6}>
                <label className="input-label">
                  Discount
                </label>
              </Col>
              <Col span={10}>
                <Form.Item name="discount" rules={[{ required: true, message: 'Please enter discount value' }]}>
                  <InputNumber
                    placeholder="0"
                    className="form-control-field custom-input-field"
                    min={0}
                    max={discountType === 'percent' ? 100 : undefined}
                    precision={discountType === 'percent' ? 0 : 2}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="type">
                  <Select
                    className="select-input-field type-select-field"
                    placeholder="Select"
                    value={discountType}
                    onChange={setDiscountType}
                  >
                    <Select.Option value="percent">% (Percent)</Select.Option>
                    <Select.Option value="fixed">$ (USD)</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={10}>
              <Col span={24}>
                <label className="input-label">
                  Total limit
                </label>
                <Form.Item name="totalLimit">
                  <InputNumber
                    placeholder="0"
                    className="form-control-field custom-input-field"
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={10}>
              <Col span={24}>
                <label className="input-label">
                  Limit per Family
                </label>
                <Form.Item name="limitPerFamily">
                  <InputNumber
                    placeholder="0"
                    className="form-control-field custom-input-field"
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <label className="input-label ">
                  Expiration date
                </label>
                <Form.Item name="expiry">
                  <DatePicker
                    className="w-100 form-control-field"
                    placeholder="Expiration date"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className="">
              <Col span={24}>
                <label className="input-label ">
                  Expiration Time
                </label>
                <Form.Item name="expiry-time">
                  <TimePicker
                    format={'HH:mm'}
                    className="w-100 form-control-field"
                    placeholder="Expiration time"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className="">
              <Col span={24}>
                <label className="input-label">
                  Notes
                </label>
                <Form.Item name="note">
                  <Input.TextArea
                    className="form-control-field"
                    placeholder="Additional information about discount"
                    autoSize={{ minRows: 3, maxRows: 6 }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end" className="mt-20px">
              <Button
                className="stepper-button"
                disabled={isLoading}
                htmlType="submit"
              >
                {isLoading ? (
                  <ButtonSpinner size={20} color="#000" />
                ) : (
                  'Save'
                )}
              </Button>
            </Row>
          </Form>
        </div>
      </Modal>
    </div>
  )
}

export default DiscountCode